import React, { useRef, useState, useEffect, Fragment, useContext, useCallback } from "react";

import { useSearchParams, useOutletContext } from "react-router-dom";

import styles from "./Chat.module.css";

import { chatApi, chatApiStream, chatInit, ChatAppResponse, CONVERSATION_UNAVAILABLE, describeChatFailure, fetchProgress, getQuestions } from "../../api";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { Answer, AnswerError } from "../../components/Answer";
import { sanitizeStreamingText } from "../../components/Answer/sanitizeStreamingText";
import { QuestionInput } from "../../components/QuestionInput";
import { DefaultQuestions, ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { ClearChatButton } from "../../components/ClearChatButton";
import { SettingsContext } from "../../Context/SettingsContext";
import { ChatIdContext } from "../../Context/ChatIdContext";
import { ChatSettingsPanel } from "../../components/SettingsPanel";

const formatFunctionName = (name: string): string => {
    // "GetChargeRatesFunction" → "Charge Rates", "Cached_GetMachineCatalogFunction" → "Machine Catalog (cached)"
    const cached = name.startsWith("Cached_");
    const clean = name.replace(/^Cached_/, "").replace(/^Get/, "").replace(/Function$/, "");
    const spaced = clean.replace(/([a-z])([A-Z])/g, "$1 $2");
    return cached ? `${spaced} (cached)` : spaced;
};

const isNotFound = (httpStatus?: number, errorStatus?: string) => httpStatus === 404 || Number(errorStatus) === 404;

const unavailableMessage = (error: unknown) => String(error).replace(/^Error:\s*/, "");

const Chat: React.FC = () => {
    const { chatSettings } = useContext(SettingsContext);
    const { setIsConversationActive, newChatTrigger } = useOutletContext<{
        setIsConversationActive: (active: boolean) => void;
        newChatTrigger: number;
    }>();
    const [currentQueryParameters, setSearchParams] = useSearchParams();
    const chatId = currentQueryParameters.get("chatId");

    const lastQuestionRef = useRef<string>("");
    const skipEffectRef = useRef(false);
    const isFirstNewChatEffect = useRef(true);
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    // The progress poll's conversation, pending timer and in-flight request, so switching or
    // leaving the conversation can stop both: a poll that outlives its conversation writes that
    // conversation's answers over the one on screen.
    const progressPollRef = useRef<{ chatId?: string; timer?: ReturnType<typeof setTimeout>; controller?: AbortController }>({});

    const [isQuestionsListLoading, setIsQuestionsListLoading] = useState<boolean>(false);
    const [questions, setQuestions] = useState<string[]>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isQuestionLoading, setIsQuestionLoading] = useState<boolean>(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [answers, setAnswers] = useState<ChatAppResponse>();
    const [streamingContent, setStreamingContent] = useState<string>("");
    const [reasoningContent, setReasoningContent] = useState<string>("");
    const [activeFunctions, setActiveFunctions] = useState<string[]>([]);
    const [completedFunctions, setCompletedFunctions] = useState<string[]>([]);
    const [streamingFiles, setStreamingFiles] = useState<{ fileId: string; dataUrl: string; mimeType?: string; filename?: string }[]>([]);

    const makeApiRequest = async (question: string) => {
        // Abort any in-flight request before starting a new one
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        lastQuestionRef.current = question;
        setIsConversationActive(true);

        error && setError(undefined);
        setIsLoading(true);
        setIsQuestionLoading(true);
        setStreamingContent("");
        setReasoningContent("");
        setActiveFunctions([]);
        setCompletedFunctions([]);
        setStreamingFiles([]);

        // Initialize chat upfront so it appears in history immediately
        const existingChatId = answers?.chatId;
        let resolvedChatId = existingChatId;

        if (!existingChatId) {
            try {
                const initRes = await chatInit(question, chatSettings, controller.signal);
                if (controller.signal.aborted) return;
                if (initRes.error || !initRes.data?.chatId) {
                    setError(new Error(initRes.error ? describeChatFailure(initRes.error) : "Failed to initialize chat"));
                    setIsLoading(false);
                    setIsQuestionLoading(false);
                    return;
                }
                resolvedChatId = initRes.data.chatId;
                skipEffectRef.current = true;
                const params = new URLSearchParams();
                params.set("chatId", resolvedChatId);
                setSearchParams(params);
            } catch (err) {
                if (controller.signal.aborted) return;
                setError(err);
                setIsLoading(false);
                setIsQuestionLoading(false);
                return;
            }
        }

        if (chatSettings.streaming) {
            chatApiStream(
                question,
                resolvedChatId,
                chatSettings,
                (type, data) => {
                    if (controller.signal.aborted) return;
                    if (type === "chatId") {
                        // chatId already set via /chat/init — no-op
                    } else if (type === "reasoning_delta") {
                        setReasoningContent(prev => prev + (data as string));
                    } else if (type === "function_call_started") {
                        const name = data as string;
                        setActiveFunctions(prev => [...prev, name]);
                    } else if (type === "function_called") {
                        const name = data as string;
                        setActiveFunctions(prev => prev.filter(f => f !== name));
                        setCompletedFunctions(prev => [...prev, name]);
                    } else if (type === "text_delta") {
                        setStreamingContent(prev => prev + (data as string));
                    } else if (type === "file_data") {
                        const payload = data as { fileId: string; mimeType: string; base64Data: string; filename?: string };
                        const dataUrl = `data:${payload.mimeType};base64,${payload.base64Data}`;
                        setStreamingFiles(prev => [...prev, { fileId: payload.fileId, dataUrl, mimeType: payload.mimeType, filename: payload.filename }]);
                    } else if (type === "completed") {
                        const completedData = data as ChatAppResponse;
                        setIsLoading(false);
                        const completedImages = completedData.choices
                            ?.flatMap(c => c.files ?? [])
                            ?.filter(f => f.content)
                            ?.map(f => ({ fileId: f.fileId, dataUrl: `data:${f.mimeType ?? "image/png"};base64,${f.content!}`, mimeType: f.mimeType, filename: f.filename }))
                            ?? [];
                        if (completedImages.length > 0) {
                            setStreamingFiles(completedImages);
                        }
                        fetchProgress(completedData.chatId, controller.signal).then(response => {
                            if (controller.signal.aborted) return;
                            const historyData = response.data ?? completedData;
                            if (response.data) {
                                historyData.inputTokens = completedData.inputTokens;
                                historyData.outputTokens = completedData.outputTokens;
                                historyData.totalTokens = completedData.totalTokens;
                            }
                            setAnswers(historyData);
                            setStreamingContent("");
                            setReasoningContent("");
                            setActiveFunctions([]);
                            setCompletedFunctions([]);
                            setStreamingFiles([]);
                            setIsQuestionLoading(false);
                        }).catch(() => {
                            if (controller.signal.aborted) return;
                            setAnswers(completedData);
                            setStreamingContent("");
                            setReasoningContent("");
                            setActiveFunctions([]);
                            setCompletedFunctions([]);
                            setStreamingFiles([]);
                            setIsQuestionLoading(false);
                        });
                    } else if (type === "error") {
                        setError(new Error(data as string));
                        setIsLoading(false);
                        setIsQuestionLoading(false);
                        setReasoningContent("");
                        setActiveFunctions([]);
                        setCompletedFunctions([]);
                        setStreamingFiles([]);
                    }
                },
                err => {
                    if (controller.signal.aborted) return;
                    setError(err);
                    setIsLoading(false);
                    setIsQuestionLoading(false);
                    setReasoningContent("");
                    setActiveFunctions([]);
                    setCompletedFunctions([]);
                    setStreamingFiles([]);
                },
                controller.signal
            );
        } else {
            chatApi(question, resolvedChatId, chatSettings, controller.signal)
                .then(async response => {
                    if (controller.signal.aborted) return;
                    if (response.error) {
                        setError(new Error(describeChatFailure(response.error)));
                    } else if (response.data) {
                        const original = response.data;
                        const full = await fetchProgress(original.chatId, controller.signal).catch(() => null);
                        if (controller.signal.aborted) return;
                        const historyData = full?.data ?? original;
                        if (full?.data) {
                            historyData.inputTokens = original.inputTokens;
                            historyData.outputTokens = original.outputTokens;
                            historyData.totalTokens = original.totalTokens;
                        }
                        setAnswers(historyData);
                    }
                })
                .catch(err => {
                    if (controller.signal.aborted) return;
                    setError(err);
                })
                .finally(() => {
                    if (controller.signal.aborted) return;
                    setIsLoading(false);
                    setIsQuestionLoading(false);
                });
        }
    };

    const stopProgressPoll = useCallback(() => {
        clearTimeout(progressPollRef.current.timer);
        progressPollRef.current.controller?.abort();
        progressPollRef.current = {};
    }, []);

    const fetchChatProgress = useCallback(
        (chatId: string) => {
            // Each round re-arms the next one itself, so a stopped poll stays stopped.
            const poll = async () => {
                stopProgressPoll();
                const controller = new AbortController();
                progressPollRef.current = { chatId, controller };

                try {
                    const response = await fetchProgress(chatId, controller.signal);
                    if (controller.signal.aborted) return;

                    if (response.status > 299 || response.error) {
                        console.error(response.error?.title + "\n" + response.error?.detail);
                        console.error(response.error);
                        if (response.error && response.error.status == "429") {
                            throw Error("Too many requests. Please wait a few seconds before trying again.");
                        }

                        if (isNotFound(response.status, response.error?.status)) {
                            throw Error(CONVERSATION_UNAVAILABLE);
                        }
                        throw Error(response.error?.title || "Unknown error");
                    }
                    if (!response.data) {
                        throw Error("No response body");
                    }
                    setAnswers(response.data);

                    if (response.data?.isCompleted) {
                        setIsLoading(false);
                    } else {
                        // Chat is still being processed in the background — poll again
                        setIsLoading(true);
                        progressPollRef.current.timer = setTimeout(poll, 3000);
                    }
                    if (response?.data?.gptError) {
                        console.error(response?.data?.gptError);
                        setError("OpenAI is experiencing performance issues. Try again later.\n");
                    }
                } catch (e) {
                    if (controller.signal.aborted) return;
                    console.error(e);
                    setIsLoading(false);

                    // Drop the conversation that failed to load and clear it from the URL ourselves. The
                    // chatId effect must be skipped: it reads a cleared chatId next to a still-loaded
                    // conversation as "New Chat" and would reset the error we are about to show.
                    skipEffectRef.current = true;
                    lastQuestionRef.current = "";
                    setAnswers(undefined);
                    setError(e);

                    const newQueryParameters: URLSearchParams = new URLSearchParams();
                    setSearchParams(newQueryParameters);
                } finally {
                    // Unless a newer load has taken over: its own finally ends the loading state.
                    if (!progressPollRef.current.controller || progressPollRef.current.controller === controller) {
                        setIsHistoryLoading(false);
                    }
                }
            };

            return poll();
        },
        [setSearchParams, stopProgressPoll]
    );

    const clearChat = () => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        stopProgressPoll();
        skipEffectRef.current = true;
        setIsConversationActive(false);

        const newQueryParameters: URLSearchParams = new URLSearchParams();
        setSearchParams(newQueryParameters);
        lastQuestionRef.current = "";
        setError(undefined);
        setAnswers(undefined);
        setStreamingContent("");
        setReasoningContent("");
        setActiveFunctions([]);
        setCompletedFunctions([]);
        setStreamingFiles([]);
        setIsLoading(false);
    };

    useEffect(() => {
        if (isFirstNewChatEffect.current) {
            isFirstNewChatEffect.current = false;
            return;
        }
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        stopProgressPoll();
        lastQuestionRef.current = "";
        setAnswers(undefined);
        setStreamingContent("");
        setReasoningContent("");
        setActiveFunctions([]);
        setCompletedFunctions([]);
        setStreamingFiles([]);
        setError(undefined);
        setIsLoading(false);
        setIsQuestionLoading(false);
    }, [newChatTrigger, stopProgressPoll]);

    // Leaving the chat page stops the request in flight and the progress poll.
    useEffect(
        () => () => {
            abortControllerRef.current?.abort();
            stopProgressPoll();
        },
        [stopProgressPoll]
    );

    useEffect(() => {
        if (isQuestionsListLoading) return;

        setIsQuestionsListLoading(true);
        getQuestions(chatSettings.noCache).then(r => setQuestions(r.status < 400 ? r.data : DefaultQuestions));
    }, [chatSettings, isQuestionsListLoading]);

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);
    useEffect(() => {
        streamingContent && chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [streamingContent]);
    useEffect(() => {
        (reasoningContent || activeFunctions.length || completedFunctions.length) &&
            chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [reasoningContent, activeFunctions, completedFunctions]);
    useEffect(() => {
        answers?.choices?.length && chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [answers?.choices?.length]);

    useEffect(() => {
        if (skipEffectRef.current) {
            skipEffectRef.current = false;
            return;
        }

        // Abort any in-flight request when the user switches chats
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;

        // Only when the conversation changes. The poll's own answer re-runs this effect for the same
        // conversation, and stopping it then would end the poll after its first round.
        if (progressPollRef.current.chatId !== chatId) {
            stopProgressPoll();
        }

        if (!chatId && answers?.chatId) {
            // Navigated to "/" (New Chat / logo) — clear the previous conversation
            lastQuestionRef.current = "";
            setAnswers(undefined);
            setStreamingContent("");
            setReasoningContent("");
            setActiveFunctions([]);
            setCompletedFunctions([]);
            setStreamingFiles([]);
            setError(undefined);
            setIsLoading(false);
            setIsQuestionLoading(false);
            return;
        }

        if (chatId && chatId != answers?.chatId) {
            lastQuestionRef.current = "";
            setIsQuestionLoading(false);
            setIsLoading(false);
            setIsHistoryLoading(true);
            setStreamingContent("");
            setReasoningContent("");
            setActiveFunctions([]);
            setCompletedFunctions([]);
            setStreamingFiles([]);
            fetchChatProgress(chatId);
        }
    }, [chatId, answers?.chatId, fetchChatProgress, stopProgressPoll]);

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    return (
        <ChatIdContext.Provider value={chatId ?? answers?.chatId}>
        <div className={styles.container}>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {isHistoryLoading ? (
                        <div className={styles.chatHistoryLoading}>
                            <Spinner label="Loading conversation..." size={SpinnerSize.large} />
                        </div>
                    ) :!lastQuestionRef.current && !answers ? (
                        <div className={styles.chatEmptyState}>
                            {/* A conversation that could not be opened lands here with the chat id already
                                dropped from the URL, so the reason has to be shown alongside the empty state. */}
                            {error ? <div className={styles.chatUnavailableNotice}>{unavailableMessage(error)}</div> : null}
                            <h2 className={styles.chatEmptyStateSubtitle}>Ask anything or try an example</h2>
                            {questions && <ExampleList questions={questions} onExampleClicked={onExampleClicked} />}
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {answers?.choices?.slice(chatSettings.showInitMessage ? 0 : 1).map((answer, index) => {
                                // User message
                                if (answer.message?.role == "user") {
                                    return (
                                        <Fragment key={index}>
                                            <UserChatMessage message={answer.message.content} createAt={answer.message.createdAt} />
                                            {answers?.instruction && index == 0 && chatSettings.showInitMessage && (
                                                <div className={styles.chatMessageGpt}>
                                                    <Answer answer={"### System prompt:\n\n" + answers?.instruction} skipTransform />
                                                </div>
                                            )}
                                        </Fragment>
                                    );
                                }

                                // Reasoning block (showFunctions gate)
                                // H. Render reasoning text through markdown (skipTransform keeps it lightweight).
                                if (answer.reasoningText) {
                                    return chatSettings.showFunctions ? (
                                        <div key={index} className={styles.reasoningBlock}>
                                            <div className={styles.reasoningLabel}>Thinking</div>
                                            <div className={styles.reasoningText}>
                                                <Answer answer={answer.reasoningText} />
                                            </div>
                                        </div>
                                    ) : null;
                                }

                                // Regular function call chip (showFunctions gate, not code_interpreter)
                                if (answer.functionName && !answer.message) {
                                    return chatSettings.showFunctions ? (
                                        <div key={index} className={styles.historyFunctionCall}>
                                            <div className={styles.functionChips}>
                                                <span className={styles.functionChipCompleted}>
                                                    {formatFunctionName(answer.functionName)} &#10003;
                                                </span>
                                            </div>
                                            {chatSettings.showFunctionsResults && (answer.functionArguments || answer.functionResult) && (
                                                <div className={styles.functionDetails}>
                                                    {answer.functionFallback && <div>Fallback: <b>{answer.functionFallback}</b></div>}
                                                    {answer.functionArguments && <div><b>Args:</b> {answer.functionArguments}</div>}
                                                    {answer.functionResult && <div><b>Result:</b> {answer.functionResult}</div>}
                                                </div>
                                            )}
                                        </div>
                                    ) : null;
                                }

                                // Regular message, code_interpreter, or items with files
                                if (answer.message?.content || answer.files || (answer.functionName === "code_interpreter" && chatSettings.showFunctions)) {
                                    return (
                                        <div className={styles.chatMessageGpt} key={index}>
                                            <Answer
                                                answer={answer.message?.content}
                                                incomplete={answer.message?.status === "incomplete"}
                                                createdAt={answer.message?.createdAt ?? answer.functionCalledAt}
                                                functionCall={
                                                    chatSettings.showFunctions && answer.functionName === "code_interpreter"
                                                        ? {
                                                              functionName: answer.functionName,
                                                              functionArguments: answer.functionArguments,
                                                              functionResult: chatSettings.showFunctionsResults ? answer.functionResult : undefined,
                                                          }
                                                        : undefined
                                                }
                                                files={answer.files}
                                            />
                                        </div>
                                    );
                                }

                                return null;
                            })}
                            {answers?.chatId && !isLoading && chatSettings.showInitMessage && (
                                <div className={styles.chatMessageGpt}>
                                    <Answer
                                        answer={
                                            "Used input tokens: " +
                                            answers.inputTokens +
                                            "\n\n" +
                                            "Used output tokens: " +
                                            answers.outputTokens +
                                            "\n\n" +
                                            "Total tokens: " +
                                            answers.totalTokens +
                                            "\n\n"
                                        }
                                    />
                                </div>
                            )}
                            {isQuestionLoading && <UserChatMessage message={lastQuestionRef.current} />}
                            {isLoading && !streamingContent && (
                                <div className={styles.streamingStatus}>
                                    {chatSettings.showFunctions && (activeFunctions.length > 0 || completedFunctions.length > 0) && (
                                        <div className={styles.functionChips}>
                                            {completedFunctions.map((name, i) => (
                                                <span key={`done-${i}`} className={styles.functionChipCompleted}>
                                                    {formatFunctionName(name)} &#10003;
                                                </span>
                                            ))}
                                            {activeFunctions.map((name, i) => (
                                                <span key={`active-${i}`} className={styles.functionChipActive}>
                                                    <Spinner size={SpinnerSize.xSmall} /> {formatFunctionName(name)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {chatSettings.showFunctions && reasoningContent && (
                                        <div className={styles.reasoningBlock}>
                                            <div className={styles.reasoningLabel}>Thinking</div>
                                            {/* H. Render live streaming reasoning through markdown. */}
                                            <div className={styles.reasoningText}>
                                                <Answer answer={reasoningContent} />
                                            </div>
                                        </div>
                                    )}
                                    <div className={styles.functionChips}>
                                        <span className={styles.functionChipThinking}>
                                            <Spinner size={SpinnerSize.xSmall} /> Thinking
                                        </span>
                                    </div>
                                </div>
                            )}
                            {streamingFiles.length > 0 && (
                                <div className={styles.chatMessageGpt}>
                                    <div className={styles.streamingImages}>
                                        {streamingFiles.map((f, i) =>
                                            !f.mimeType || f.mimeType.startsWith("image/") ? (
                                                <img key={i} src={f.dataUrl} alt="Chart" className={styles.streamingImage} />
                                            ) : (
                                                <a key={i} href={f.dataUrl} download={f.filename ?? "file"} className={styles.streamingDownload}>
                                                    {f.filename ?? "Download file"}
                                                </a>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                            {streamingContent && (
                                <div className={styles.chatMessageGpt}>
                                    {/* Strip citation markers / neutralize unresolved sandbox: refs while streaming
                                        so raw markup never flashes before the resolved message swaps in. */}
                                    <Answer answer={sanitizeStreamingText(streamingContent)} />
                                </div>
                            )}
                            {error ? (
                                <>
                                    {!answers?.choices?.length && <UserChatMessage message={lastQuestionRef.current} />}
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError
                                            error={error.toString()}
                                            errorTime={answers?.gptErrorTime}
                                            onRetry={() => makeApiRequest(lastQuestionRef.current)}
                                        />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}

                    <div className={styles.chatInput}>
                        {(lastQuestionRef.current || answers) && (
                            <div className={styles.commandsContainer}>
                                <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={isLoading} />
                            </div>
                        )}
                        <QuestionInput clearOnSend placeholder="Type a new question" disabled={isLoading} onSend={question => makeApiRequest(question)} />
                    </div>
                </div>
                <ChatSettingsPanel />
            </div>
        </div>
        </ChatIdContext.Provider>
    );
};

export default Chat;
